import React, { memo, useCallback, useEffect, useRef } from "react";
import { proxy, useSnapshot } from "valtio";

import classNames from "classnames";
import { useAppContext } from "../../hooks/useAppContext";
import { useGoogleAnalytics } from "../../hooks/useGoogleAnalytics";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { useLabels } from "../../hooks/useLabels";
import { usePrice } from "../../hooks/usePrice";
import { useResizeObserver } from "../../hooks/useResizeObserver";
import { showPriceDialog } from "../dialogs/price-dialog";
import { Logo } from "../logo";
import { TransparentButton } from "../transparent-button";

import { Header } from "./header";
import { labels } from "./labels";
import { IconButton } from "../icon-button";
import { useKeyDown } from "../../hooks/useKeyboard";
import AddToCartButton from "./add-to-cart-button";
import { useIsMobileDevice } from "../../hooks/useIsMobileDevice";
import { CommandHistory } from "../command-history";
import { useShowDesktopLayout } from "../../hooks/useShowdesktopLayout";
import { HamburgerMenu } from "../hamburger-menu/hamburger-menu";
import { useBack } from "../floating-menus/back-menu-item";
import { Icon } from "../icon";
import { useLogAiAssistedPreviewExit } from "../../hooks/use-log-ai-assisted-preview-exit";

const headerState = proxy({
	isLargeLogo: true,
	isLargeButton: true,
	actions: {
		setIsLargeLogo(value: boolean) {
			headerState.isLargeLogo = value;
		},
		setIsLargeButton(value: boolean) {
			headerState.isLargeButton = value;
		},
	},
});

const HeaderPreviewLeft = () => {
	const googleAnalytics = useGoogleAnalytics();
	const [, setIsPreviewMode] = useIsPreviewMode();
	const { logGaEvent } = useLogAiAssistedPreviewExit();

	function onClose() {
		googleAnalytics.trackEventToGa({
			trackingAction: "PreviewClosed",
		});

		logGaEvent({
			trackingAction: "AIAssistant.Preview.EditClicked",
		});
		setIsPreviewMode(false);
	}

	// Escape to close preview mode
	useKeyDown((event) => {
		if (event.key === "Escape") {
			onClose();
		}
	});

	const [[previewBackText], areLabelsLoading] = useLabels([labels.previewBack]);

	if (areLabelsLoading) return null;
	return (
		<div className="header__left__wrapper">
			<TransparentButton
				className="header__button-back"
				onClick={() => {
					onClose();
				}}
			>
				<Icon iconName="angle-left-slim" />
				{previewBackText}
			</TransparentButton>
		</div>
	);
};

const HeaderDefaultLeft = () => {
	const showDesktopLayout = useShowDesktopLayout();

	const [, onBackClick] = useBack();

	const containerRef = useRef<HTMLDivElement>(null);
	const backButtonRef = useRef<HTMLDivElement>(null);

	const { isLargeLogo } = useSnapshot(headerState);

	const checkLogoSize = () => {
		if (!containerRef.current || !backButtonRef.current) return;

		const wrapperWidth = containerRef.current.clientWidth;
		const backButtonWidth = backButtonRef.current.offsetWidth;

		const newIsLargeLogo = headerState.isLargeButton
			? wrapperWidth > 140 + backButtonWidth
			: false;
		headerState.actions.setIsLargeLogo(newIsLargeLogo);
	};

	useResizeObserver(containerRef, checkLogoSize);

	const styles = classNames("header__left__wrapper", {
		"header__left__wrapper--small": !isLargeLogo,
	});

	return (
		<div ref={containerRef} className={styles}>
			<div
				ref={backButtonRef}
				className={showDesktopLayout ? "header-default-left__button" : ""}
			>
				{showDesktopLayout ? (
					<HamburgerMenu />
				) : (
					<IconButton
						iconName="angle-left-slim"
						onClick={() => {
							onBackClick?.();
						}}
					/>
				)}
			</div>
			<Logo showLargeLogo={isLargeLogo} />
		</div>
	);
};

const HeaderRight = () => {
	const { googleAnalytics } = useAppContext();
	const { logGaEvent } = useLogAiAssistedPreviewExit();
	const shouldValidate = useRef(true);
	const [isPreviewMode] = useIsPreviewMode();
	const isMobileDevice = useIsMobileDevice();

	const showDesktopLayout = useShowDesktopLayout();

	const price = usePrice();

	useEffect(() => {
		if (!isPreviewMode) shouldValidate.current = true;
	}, [isPreviewMode]);

	const wrapperRef = useRef<HTMLDivElement>(null);
	const priceRef = useRef<HTMLDivElement>(null);
	const ctaTextButtonRef = useRef<HTMLButtonElement>(null);

	const { isLargeButton } = useSnapshot(headerState);

	// When available width is larger than button width, set showLongCta boolean
	const checkSize = useCallback(() => {
		if (!ctaTextButtonRef.current || !wrapperRef.current || !priceRef.current) return;

		const { offsetWidth: textButtonWidth } = ctaTextButtonRef.current;
		const { offsetWidth: priceWidth } = priceRef.current;
		const { clientWidth: wrapperWidth } = wrapperRef.current;

		const isNewLargeButton =
			headerState.isLargeLogo || wrapperWidth > textButtonWidth + priceWidth;
		headerState.actions.setIsLargeButton(isNewLargeButton);
	}, []);

	useResizeObserver(wrapperRef, checkSize);

	useEffect(() => {
		if (!price) return;

		checkSize();
	}, [price, checkSize]);

	return (
		<div ref={wrapperRef} className="header__right__wrapper">
			{showDesktopLayout && !isPreviewMode && <CommandHistory elevated={false} />}
			<div ref={priceRef}>
				<TransparentButton
					className="header__price"
					onClick={() => {
						if (price) {
							googleAnalytics.trackEventToGa({
								trackingAction: "PriceClicked",
							});

							logGaEvent({
								trackingAction: "AIAssistant.Preview.AddToCartClicked",
							});

							// eslint-disable-next-line @typescript-eslint/no-floating-promises
							showPriceDialog();
						}
					}}
					role="button"
				>
					{price?.priceWithoutDiscount &&
					price.priceWithoutDiscount !== price.priceWithDiscount ? (
						<span className="header__price--without-discount">
							{price.currency?.formatPriceAmount(price.priceWithoutDiscount)}
						</span>
					) : (
						""
					)}
					{price?.priceWithDiscount ? (
						<span className="header__price--with-discount">
							{price.currency.formatPriceAmount(price.priceWithDiscount)}
						</span>
					) : (
						""
					)}
					{showDesktopLayout && (
						<Icon iconName="less-than" className="header__price-icon-rotated" />
					)}
				</TransparentButton>
			</div>
			{(!isPreviewMode || !isMobileDevice) && (
				<AddToCartButton
					showLargeButton={isLargeButton}
					ctaTextButtonRef={ctaTextButtonRef}
				/>
			)}
		</div>
	);
};

export const HeaderContainer = memo(() => {
	const [isPreviewMode] = useIsPreviewMode();
	return isPreviewMode ? <PreviewHeaderContainer /> : <DefaultHeaderContainer />;
});
HeaderContainer.displayName = "HeaderContainer";

function DefaultHeaderContainer() {
	return <Header left={<HeaderDefaultLeft />} right={<HeaderRight />}></Header>;
}

function PreviewHeaderContainer() {
	/** for large screens we center, for small screens we distribute space evenly */
	const showDesktopLayout = useShowDesktopLayout();

	return (
		<Header left={<HeaderPreviewLeft />} right={<HeaderRight />} center={showDesktopLayout} />
	);
}
